import { isPrizeCode } from './prizeCode'

export interface CustomerEmailSyncOptions {
  background?: boolean
  isCurrent?: () => boolean
  /** Close a native wallet sheet before showing the browser confirmation. */
  beforeConfirm?: () => void
  reviewBeforePayment?: boolean
}

export class CustomerEmailSyncCancelled extends Error {
  readonly code = 'CUSTOMER_EMAIL_SYNC_CANCELLED'
}

export class PrizeEmailCheckFailed extends Error {
  readonly code = 'PRIZE_EMAIL_CHECK_FAILED'
}

export function isCustomerEmailSyncCancelled(error: unknown): boolean {
  return (error as { code?: string } | null)?.code === 'CUSTOMER_EMAIL_SYNC_CANCELLED'
}

/** One gate for background saves, checkout submit and wallets, before any order write. */
export function createPrizeEmailGuard<Customer extends { emailAddress: string }, Result>(dependencies: {
  context: () => { id: string, couponCodes: readonly string[] } | null | undefined
  matches: (emailAddress: string) => Promise<boolean>
  confirm: (emailAddress: string) => Promise<boolean>
  dismiss: () => void
  write: (customer: Customer) => Promise<Result>
}) {
  let revision = 0
  let declined: string | null = null
  let matched: string | null = null
  let writes: Promise<unknown> = Promise.resolve()
  let pending: {
    key: string
    options: CustomerEmailSyncOptions
    confirming: boolean
    promise: Promise<Result>
  } | null = null

  function contextKey() {
    const order = dependencies.context()
    return JSON.stringify([order?.id, [...(order?.couponCodes ?? [])].sort()])
  }

  function emailEdited() {
    revision++
    declined = null
    pending = null
    dependencies.dismiss()
  }

  function sync(customer: Customer, options: CustomerEmailSyncOptions = {}): Promise<Result> {
    const context = contextKey()
    const key = JSON.stringify([context, customer])
    if (pending?.key === key) {
      pending.options.background &&= options.background
      pending.options.isCurrent = options.isCurrent ?? pending.options.isCurrent
      pending.options.reviewBeforePayment ||= options.reviewBeforePayment
      if (options.beforeConfirm) {
        pending.options.beforeConfirm = options.beforeConfirm
        if (pending.confirming)
          options.beforeConfirm()
      }
      return pending.promise
    }

    const attempt = ++revision
    dependencies.dismiss()
    const entry = { key, options: { ...options }, confirming: false, promise: null as unknown as Promise<Result> }
    const current = () => attempt === revision && contextKey() === context && (entry.options.isCurrent?.() ?? true)
    const assertCurrent = () => {
      if (!current())
        throw new CustomerEmailSyncCancelled()
    }

    entry.promise = (async () => {
      let confirmed = false
      const emailKey = JSON.stringify([context, customer.emailAddress.trim().toLowerCase()])
      if (dependencies.context()?.couponCodes.some(isPrizeCode) && matched !== emailKey) {
        if (declined === emailKey && entry.options.background)
          throw new CustomerEmailSyncCancelled()
        let matches: boolean
        try {
          matches = await dependencies.matches(customer.emailAddress)
        }
        catch {
          assertCurrent()
          throw new PrizeEmailCheckFailed()
        }
        assertCurrent()
        if (matches) {
          matched = emailKey
        }
        else {
          entry.confirming = true
          entry.options.beforeConfirm?.()
          confirmed = await dependencies.confirm(customer.emailAddress.trim())
          assertCurrent()
          if (!confirmed) {
            declined = emailKey
            throw new CustomerEmailSyncCancelled()
          }
        }
      }

      // A previous customer mutation may already be in flight. Never let an older
      // response arrive after this one and restore the wrong email or cart total.
      const write = writes.catch(() => {}).then(async () => {
        assertCurrent()
        return dependencies.write(customer)
      })
      writes = write
      const result = await write
      // Payment must be submitted again with the reviewed new amount, never
      // charge straight after removing a reward inside a browser dialog.
      if (confirmed && entry.options.reviewBeforePayment)
        throw new CustomerEmailSyncCancelled()
      return result
    })().finally(() => {
      if (pending === entry)
        pending = null
    })
    pending = entry
    return entry.promise
  }

  return { sync, emailEdited }
}
